/* eslint-disable no-magic-numbers */
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useLocation } from 'react-router-dom';

import {
  getPendoVisitorId,
  initializePendoUser,
  isPendoTempId,
  loadTealium,
  loadTealiumSync,
  reInitializePendoUser,
  trackPageView,
  updatePendoUser
} from '@futrli/tracking';
import { isPending, Logger, matchAnyReportingInternalRenderReportUrl, ROUTES } from '@futrli/shared-services';
import { LOCALE_STORAGE_KEY } from '@futrli/localisation';

import { AppGlobalStyles } from '../app-global-styles';
import { AuthenticatedRoutes } from '../routes/authenticated';
import { checkIfAuthenticated } from '../service';
import { FederatedSignInRedirectManager } from '../../federated-sign-in/federated-sign-in-redirect-manager';
import { LAST_ACTIVITY_KEY, StorageBackedNumber } from '../storage';
import { SessionTimeoutManager } from '../components/session-timeout-manager';
import { signOut } from '../../login/redux-utils/actions';
import { StoreState } from '../../core/types';
import { UnauthenticatedRoutes } from '../routes/unauthenticated';
import { useAuthBootstrap } from '../hooks/use-auth-bootstrap';
import { useBusinessType, useCurrentUserEmail, useCurrentUserId } from '../../../../predict/src/dashboard/hooks';

interface Props {
  sessionDurationMs: number;
  component: React.ComponentType;
  isInternalReportPage?: boolean;
}

export const SESSION_STORAGE_REDIRECT_PATH = 'redirect-path';

/**
 * Represents the root component of the App, managing also the routing of it.
 */
export const AuthContainer: React.FC<Props> = ({ sessionDurationMs, component }: Props) => {
  const firstRender = useRef(true);

  const location = useLocation();

  const flags = useFlags();

  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: StoreState) => checkIfAuthenticated(state.authReducers));
  const isCurrenAuthenticatedUserLoading = useSelector((state: StoreState) =>
    isPending(state.loadingRequests.GET_CURRENT_AUTHENTICATED_USER)
  );
  const isInternalReportPage = matchAnyReportingInternalRenderReportUrl(window.location.pathname);

  const currentUserId = useCurrentUserId();
  const currentUserEmail = useCurrentUserEmail();
  const businessType = useBusinessType();

  useEffect(() => {
    // we make sure to only delete userLocale from localStorage if flags are loaded and not active
    // this is temporary until we remove flags.switchLocale flag
    setTimeout(() => {
      if (isAuthenticated && typeof flags.switchLocale === 'boolean' && !flags.switchLocale) {
        localStorage.removeItem(LOCALE_STORAGE_KEY);
      }
    }, 2000);
  }, [flags.switchLocale, isAuthenticated]);

  // Reference to last known activity on a session (persisted in localStorage)
  const lastActivityStorage = useRef(new StorageBackedNumber(LAST_ACTIVITY_KEY));

  const handleSignOut = () => {
    dispatch(signOut());
  };

  // remember route user is trying to get
  if (
    !firstRender.current &&
    !isCurrenAuthenticatedUserLoading &&
    !isAuthenticated &&
    location.pathname.startsWith(ROUTES.WORKSPACES_ROUTE)
  ) {
    sessionStorage.setItem(SESSION_STORAGE_REDIRECT_PATH, location.pathname);
  }

  useEffect(() => {
    if (window.location.hostname === 'localhost') {
      return;
    }

    if (isAuthenticated && !isCurrenAuthenticatedUserLoading && !isInternalReportPage) {
      const currentPendoVisitorId = getPendoVisitorId();
      if (typeof currentPendoVisitorId === 'undefined') {
        Logger.info('[Pendo Debug] No existing Pendo Visitor ID. Initializing user...');
        initializePendoUser(currentUserId, currentUserEmail, businessType);
      } else if (isPendoTempId(currentPendoVisitorId)) {
        Logger.info('[Pendo Debug] Existing Pendo visitor found with temporary Pendo ID. Reinitializing user...');
        reInitializePendoUser(currentUserId, currentUserEmail, businessType);
      } else {
        Logger.info('[Pendo Debug] Existing Visitor ID found. Updating user...');
        updatePendoUser(currentUserId, currentUserEmail, businessType);
      }

      // Track initial page view
      trackPageView();
    }
  }, [
    businessType,
    currentUserEmail,
    currentUserId,
    isAuthenticated,
    isCurrenAuthenticatedUserLoading,
    isInternalReportPage
  ]);

  useEffect(() => {
    // Load 2 Tealium scripts
    loadTealium();
    loadTealiumSync();
  }, []);

  useEffect(() => {
    if (isAuthenticated && !isCurrenAuthenticatedUserLoading && window.pendo && window.pendo.pageLoad) {
      // Track subsequent page loads
      trackPageView();
    }
  }, [isAuthenticated, isCurrenAuthenticatedUserLoading, location.pathname]);

  useEffect(() => {
    firstRender.current = false;
  }, []);

  const { isLoadingData } = useAuthBootstrap({
    lastActivityStorage: lastActivityStorage.current,
    sessionDurationMs
  });

  /*
   * The federated sign-in redirect URL must always be handled by the
   * FederatedSignInRedirectManager, regardless of the current authentication
   * state. Under aws-amplify v6 the OAuth code-exchange runs as soon as
   * `getCurrentAuthenticatedUser()` is called (above), which flips
   * `isAuthenticated` to `true` before the manager (which only lives in
   * `UnauthenticatedRoutes`) ever gets a chance to mount. Mounting it
   * unconditionally here ensures its Hub subscription is in place before the
   * code-exchange completes, and keeps it mounted across the auth-state flip
   * so it can navigate the user on to their landing page.
   */
  if (location.pathname === ROUTES.FEDERATED_SIGN_IN_REDIRECT) {
    return (
      <>
        <AppGlobalStyles />
        <FederatedSignInRedirectManager />
      </>
    );
  }

  if (isLoadingData || isCurrenAuthenticatedUserLoading) {
    return <React.Fragment />;
  }

  if (isAuthenticated) {
    return (
      <React.Fragment>
        <SessionTimeoutManager
          sessionDurationMs={sessionDurationMs}
          lastActivityStorage={lastActivityStorage.current}
          signOut={handleSignOut}
        />
        <AppGlobalStyles />
        <AuthenticatedRoutes component={component} />
      </React.Fragment>
    );
  }

  return (
    <>
      <AppGlobalStyles />
      <UnauthenticatedRoutes />
    </>
  );
};
